import type { SessionGuidance, SessionInfo } from '@web-app/types/session-prep';
import { trpc } from '@web-app/utils/trpc';

const STALE_TIME = 5 * 60 * 1000;

/**
 * Fetches session info (session number) and guidance (focus label + guidance
 * text) for a patient-provider pair.
 *
 * Standalone hook — works in any context. The server counts past occurred
 * appointments to derive the session number, then maps it to a framework-
 * defined guidance entry.
 */
export function useSessionGuidance(patientId: string | null | undefined) {
  const { data, isPending, error } =
    trpc.sessionPrep.getInfoAndGuidance.useQuery(
      { patientId: patientId ?? '' },
      {
        enabled: !!patientId,
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    );

  const sessionInfo: SessionInfo | null = data?.sessionNumber
    ? { sessionNumber: data.sessionNumber }
    : null;

  const sessionGuidance: SessionGuidance | null = data?.guidance ?? null;

  return {
    sessionInfo,
    sessionGuidance,
    isLoading: !!patientId && isPending,
    error: (error as Error | null) ?? null,
  };
}
